import { Link } from 'react-router-dom';
import { Brain, ArrowRight, Bell, CheckCircle, Sparkles, Award, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState } from 'react';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

const aiCertifications = {
  microsoft: {
    provider: 'Microsoft Azure',
    color: 'from-blue-500 to-blue-700',
    bgColor: 'bg-blue-50',
    programs: [
      {
        code: 'AZ-900',
        name: 'Azure Fundamentals',
        level: 'Foundational',
        duration: '4-6 weeks',
        description: 'Understand cloud concepts, Azure services, security, privacy, compliance, and pricing.',
        topics: ['Cloud concepts', 'Azure architecture', 'Azure services', 'Security & compliance', 'Azure management'],
        popular: true
      },
      {
        code: 'AI-900',
        name: 'Azure AI Fundamentals',
        level: 'Foundational',
        duration: '4-6 weeks',
        description: 'Learn AI concepts and Azure AI services fundamentals.',
        topics: ['AI workloads & considerations', 'Machine Learning on Azure', 'Computer Vision', 'NLP', 'Conversational AI'],
        popular: true
      },
      {
        code: 'AI-102',
        name: 'Azure AI Engineer Associate',
        level: 'Associate',
        duration: '8-10 weeks',
        description: 'Design and implement AI solutions using Azure Cognitive Services, Azure Bot Service, and Azure Machine Learning.',
        topics: ['Azure Cognitive Services', 'Knowledge Mining', 'Document Intelligence', 'Generative AI solutions', 'Azure OpenAI Service'],
        popular: true
      },
      {
        code: 'DP-100',
        name: 'Azure Data Scientist Associate',
        level: 'Associate',
        duration: '10-12 weeks',
        description: 'Apply machine learning to train, evaluate, and deploy models using Azure Machine Learning.',
        topics: ['ML workspace management', 'Data exploration', 'Model training', 'MLOps', 'Responsible AI'],
        popular: true
      },
      {
        code: 'AB-900',
        name: 'Copilot & Agent Administration Fundamentals',
        level: 'Foundational',
        duration: '4-6 weeks',
        description: 'Administer Microsoft 365 Copilot and agents — identity, Purview data protection, licensing, and adoption oversight.',
        topics: ['M365 admin centers & identity', 'Microsoft Purview & DSPM for AI', 'Copilot license & PAYG management', 'Agent governance', 'Responsible AI principles'],
        popular: true
      }
    ]
  },
  aws: {
    provider: 'Amazon Web Services',
    color: 'from-orange-500 to-orange-700',
    bgColor: 'bg-orange-50',
    programs: [
      {
        code: 'MLS-C01',
        name: 'AWS Machine Learning Specialty',
        level: 'Specialty',
        duration: '10-12 weeks',
        description: 'Design, implement, deploy, and maintain ML solutions on AWS.',
        topics: ['Data Engineering', 'Exploratory Data Analysis', 'Modeling', 'ML Implementation', 'Amazon SageMaker'],
        popular: true
      },
      {
        code: 'AIF-C01',
        name: 'AWS AI Practitioner',
        level: 'Foundational',
        duration: '4-6 weeks',
        description: 'Demonstrate foundational knowledge of AI/ML concepts and AWS AI services.',
        topics: ['AI/ML fundamentals', 'Generative AI concepts', 'AWS AI services', 'Responsible AI', 'Security & compliance'],
        popular: true
      }
    ]
  },
  google: {
    provider: 'Google Cloud',
    color: 'from-green-500 to-green-700',
    bgColor: 'bg-green-50',
    programs: [
      {
        code: 'ML-Engineer',
        name: 'Professional ML Engineer',
        level: 'Professional',
        duration: '12-14 weeks',
        description: 'Design, build, and productionize ML models on Google Cloud.',
        topics: ['ML problem framing', 'ML solution architecture', 'Data preparation', 'Model development', 'ML pipelines'],
        popular: true
      },
      {
        code: 'Cloud-Digital-Leader',
        name: 'Cloud Digital Leader',
        level: 'Foundational',
        duration: '4-6 weeks',
        description: 'Understand Google Cloud capabilities including AI and ML services.',
        topics: ['Digital transformation', 'Google Cloud products', 'AI/ML basics', 'Data & analytics', 'Security fundamentals'],
        popular: false
      }
    ]
  },
  industry: {
    provider: 'Industry Certifications',
    color: 'from-violet-500 to-purple-700',
    bgColor: 'bg-violet-50',
    programs: [
      {
        code: 'TF-Developer',
        name: 'TensorFlow Developer Certificate',
        level: 'Associate',
        duration: '8-10 weeks',
        description: 'Demonstrate proficiency in using TensorFlow to solve deep learning and ML problems.',
        topics: ['TensorFlow fundamentals', 'Neural networks', 'CNNs for image classification', 'NLP with TensorFlow', 'Time series forecasting'],
        popular: true
      },
      {
        code: 'Prompt-Engineer',
        name: 'Certified Prompt Engineer',
        level: 'Associate',
        duration: '4-6 weeks',
        description: 'Master the art of crafting effective prompts for generative AI models.',
        topics: ['Prompt engineering principles', 'ChatGPT & GPT-4', 'Claude & Gemini', 'Chain-of-thought prompting', 'Enterprise applications'],
        popular: true
      },
      {
        code: 'LangChain',
        name: 'LangChain & LLM Applications',
        level: 'Associate',
        duration: '6-8 weeks',
        description: 'Build production-ready applications with Large Language Models.',
        topics: ['LangChain framework', 'RAG applications', 'Agents & tools', 'Vector databases', 'LLM orchestration'],
        popular: true
      }
    ]
  }
};

const whyAICertification = [
  { icon: TrendingUp, title: 'High Demand', desc: 'AI roles grew 74% annually over the past 4 years' },
  { icon: Award, title: '$150K+ Salaries', desc: 'AI engineers command premium compensation' },
  { icon: Sparkles, title: 'Future-Proof', desc: 'AI is transforming every industry globally' },
  { icon: Brain, title: 'Innovation Leader', desc: 'Be at the forefront of technological change' },
];

export default function AIPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState('microsoft');

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'Interested',
        last_name: 'User',
        email,
        message: 'Interested in AI Technologies training programs',
        source: 'ai_waitlist'
      });
      toast.success('You\'re on the list! We\'ll notify you when AI programs launch.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const allPrograms = Object.values(aiCertifications).flatMap(p => p.programs);
  const popularPrograms = allPrograms.filter(p => p.popular);

  return (
    <div className="min-h-screen pt-24" data-testid="ai-page">
      {/* AI-900 Live Banner */}
      <section className="bg-gradient-to-r from-violet-600 to-purple-700 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-medium animate-pulse">LIVE NOW</span>
              <span className="font-medium">AI-900 Azure AI Fundamentals Exam Coach is available!</span>
            </div>
            <Link to="/ai-900-exam-prep">
              <Button className="bg-white text-violet-700 hover:bg-violet-50 rounded-full px-6" data-testid="ai900-banner-btn">
                Start Free Practice <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-violet-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-medium mb-6">
                Coming Soon — 10+ Certifications
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Get Certified in <span className="text-violet-600">AI Technologies</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Master artificial intelligence with industry-leading certifications from Microsoft, AWS, Google Cloud, and more. 
                Our instructor-led programs prepare you for the AI-driven future.
              </p>
              
              <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mb-8">
                <Input 
                  type="email" 
                  placeholder="Enter your email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                  data-testid="ai-notify-email"
                />
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-6" disabled={loading}>
                  <Bell className="w-4 h-4 mr-2" />
                  {loading ? 'Subscribing...' : 'Notify Me'}
                </Button>
              </form>

              {/* Provider logos */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                <span>Certifications from:</span>
                <span className="font-semibold text-blue-600">Microsoft</span>
                <span className="font-semibold text-orange-600">AWS</span>
                <span className="font-semibold text-green-600">Google</span>
                <span className="font-semibold text-violet-600">+ More</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {popularPrograms.slice(0, 4).map((program, idx) => (
                <div key={program.code} className={`${idx === 0 ? 'col-span-2' : ''} bg-white rounded-xl p-4 shadow-sm border border-slate-100`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-mono text-violet-600">{program.code}</span>
                      <h3 className="font-semibold text-slate-900 text-sm mt-1">{program.name}</h3>
                      <span className="text-xs text-slate-500">{program.level}</span>
                    </div>
                    {program.popular && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Popular</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Certification */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyAICertification.map((item) => (
              <div key={item.title} className="flex items-start space-x-3">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications by Provider */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">AI Certifications by Provider</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose from world-renowned certification programs across major cloud providers and industry standards
            </p>
          </div>

          <Tabs defaultValue="microsoft" value={selectedProvider} onValueChange={setSelectedProvider}>
            <TabsList className="flex flex-wrap justify-center gap-2 mb-10 bg-transparent">
              {Object.entries(aiCertifications).map(([key, data]) => (
                <TabsTrigger 
                  key={key} 
                  value={key}
                  className={`px-6 py-3 rounded-full border border-slate-200 data-[state=active]:border-violet-500 data-[state=active]:bg-violet-50 data-[state=active]:text-violet-700`}
                >
                  {data.provider}
                </TabsTrigger>
              ))}
            </TabsList>

            {Object.entries(aiCertifications).map(([key, data]) => (
              <TabsContent key={key} value={key}>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.programs.map((program) => (
                    <Card key={program.code} className="border-2 border-dashed border-slate-200 hover:border-violet-300 transition-colors">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${data.color} flex items-center justify-center`}>
                            <Brain className="w-6 h-6 text-white" />
                          </div>
                          {program.popular && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">Popular</span>
                          )}
                        </div>
                        
                        <span className="text-sm font-mono text-violet-600">{program.code}</span>
                        <h3 className="font-bold text-lg text-slate-900 mt-1 mb-2">{program.name}</h3>
                        <p className="text-sm text-slate-600 mb-4">{program.description}</p>
                        
                        <div className="flex items-center gap-2 mb-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${data.bgColor} text-slate-700`}>
                            {program.level}
                          </span>
                          <span className="text-xs text-slate-500">{program.duration}</span>
                        </div>

                        <div className="border-t border-slate-100 pt-4">
                          <p className="text-xs font-medium text-slate-500 mb-2">Topics Covered</p>
                          <ul className="space-y-1">
                            {program.topics.slice(0, 3).map((topic) => (
                              <li key={topic} className="flex items-center text-xs text-slate-600">
                                <CheckCircle className="w-3 h-3 text-violet-500 mr-2 flex-shrink-0" />
                                {topic}
                              </li>
                            ))}
                            {program.topics.length > 3 && (
                              <li className="text-xs text-violet-600">+{program.topics.length - 3} more topics</li>
                            )}
                          </ul>
                        </div>

                        <div className="mt-4 pt-4 border-t border-slate-100">
                          {program.code === 'AI-900' ? (
                            <Link to="/ai-900-exam-prep">
                              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid={`start-${program.code.toLowerCase()}`}>
                                Start Exam Prep <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : program.code === 'AI-102' ? (
                            <Link to="/ai-102-exam-prep">
                              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid={`start-${program.code.toLowerCase()}`}>
                                Start Exam Prep <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : program.code === 'AZ-900' ? (
                            <Link to="/az-900-exam-prep">
                              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid={`start-${program.code.toLowerCase()}`}>
                                Start Exam Prep <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : program.code === 'DP-100' ? (
                            <Link to="/dp-100-exam-prep">
                              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full" data-testid={`start-${program.code.toLowerCase()}`}>
                                Start Exam Prep <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : program.code === 'AB-900' ? (
                            <Link to="/ab-900-exam-prep">
                              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full" data-testid={`start-${program.code.toLowerCase()}`}>
                                Start Exam Prep <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          ) : (
                            <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                              Launching Q2 2026
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* All Certifications Summary */}
      <section className="py-16 bg-violet-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">Complete AI Certification Lineup</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(aiCertifications).map(([key, data]) => (
              <div key={key} className="bg-white rounded-xl p-5 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                  <span className={`w-3 h-3 rounded-full bg-gradient-to-r ${data.color} mr-2`}></span>
                  {data.provider}
                </h3>
                <ul className="space-y-2">
                  {data.programs.map((p) => (
                    <li key={p.code} className="text-sm text-slate-600 flex items-center">
                      <span className="font-mono text-xs text-violet-600 mr-2">{p.code}</span>
                      <span className="truncate">{p.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Want Training Now?</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Our ERP programs are available today with live instruction. Start your tech career journey while we prepare AI courses.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/programs">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
                View ERP Programs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
